/**
 * Sprint TF-1 Phase 1 — Open Case dialog.
 *
 * Reused by ClientTalentFix and AdminTalentFix to open a Stage-1 case.
 * Adapts the form based on origin type (signal_driven / manual / preventive_advisory).
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatApiError } from '@/lib/api';

const ORIGINS = ['signal_driven', 'manual', 'preventive_advisory'];
const SCOPES  = ['individual', 'multi_individual', 'department', 'multi_department', 'organization'];
const SEVERITIES = ['informational', 'moderate', 'severe', 'critical'];
const PRIORITIES = ['low', 'medium', 'high', 'critical'];
const SCOPES_C   = ['standard', 'restricted', 'privileged'];   // legal_hold = HM Admin only

export default function OpenCaseDialog({ open, onOpenChange, onSubmit, isAdmin = false, companyId = null }) {
  const { t } = useTranslation();
  const [originType, setOriginType] = useState('manual');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [scopeUnit, setScopeUnit] = useState('department');
  const [severity, setSeverity] = useState('moderate');
  const [strategicPriority, setStrategicPriority] = useState('medium');
  const [targetHorizon, setTargetHorizon] = useState(12);
  const [sponsor, setSponsor] = useState('');
  const [confidentiality, setConfidentiality] = useState('standard');
  const [submitting, setSubmitting] = useState(false);

  const scopeChoices = useMemo(
    () => (isAdmin ? [...SCOPES_C, 'legal_hold'] : SCOPES_C),
    [isAdmin],
  );

  const reset = useCallback(() => {
    setOriginType('manual');
    setTitle('');
    setDescription('');
    setScopeUnit('department');
    setSeverity('moderate');
    setStrategicPriority('medium');
    setTargetHorizon(12);
    setSponsor('');
    setConfidentiality('standard');
  }, []);

  const isPreventive = originType === 'preventive_advisory';

  const handleSubmit = useCallback(async () => {
    if (title.trim().length < 4) {
      toast.error(t('talentFix.titleLabel'));
      return;
    }
    if (description.trim().length < 10) {
      toast.error(t('talentFix.description'));
      return;
    }
    const body = {
      origin_type: originType,
      title: title.trim(),
      description: description.trim(),
      scope_unit: scopeUnit,
      confidentiality_scope: confidentiality,
    };
    if (isPreventive) {
      body.strategic_priority = strategicPriority;
      body.target_horizon_months = Number(targetHorizon) || 12;
      if (sponsor.trim()) body.executive_sponsor_name = sponsor.trim();
    } else {
      body.severity = severity;
    }
    if (isAdmin && companyId) body.company_id = companyId;
    setSubmitting(true);
    try {
      await onSubmit(body);
      toast.success(t('talentFix.successOpen'));
      reset();
      onOpenChange(false);
    } catch (e) {
      toast.error(t('talentFix.errOpen'), { description: formatApiError(e) });
    } finally {
      setSubmitting(false);
    }
  }, [originType, title, description, scopeUnit, severity, strategicPriority,
      targetHorizon, sponsor, confidentiality, isAdmin, companyId, isPreventive,
      onSubmit, onOpenChange, t, reset]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tf-open-case-dialog">
        <DialogHeader>
          <DialogTitle>{t('talentFix.newCase')}</DialogTitle>
          <DialogDescription>{t('talentFix.originHint.' + originType)}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Origin type */}
          <div>
            <Label className="text-xs uppercase tracking-wider">{t('talentFix.originType')}</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {ORIGINS.map((o) => (
                <button
                  key={o}
                  type="button"
                  onClick={() => setOriginType(o)}
                  data-testid={`tf-origin-${o}`}
                  className={`p-3 rounded-lg border text-sm text-left transition-smooth ${
                    originType === o
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-border hover:border-primary/40 hover:bg-muted/30'
                  }`}
                >
                  <p className="font-semibold">{t(`talentFix.origin.${o}`)}</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-snug">{t(`talentFix.originHint.${o}`)}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Title */}
          <div>
            <Label htmlFor="tf-title" className="text-xs uppercase tracking-wider">{t('talentFix.titleLabel')}</Label>
            <Input
              id="tf-title"
              data-testid="tf-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('talentFix.titlePlaceholder')}
              maxLength={200}
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="tf-desc" className="text-xs uppercase tracking-wider">{t('talentFix.description')}</Label>
            <Textarea
              id="tf-desc"
              data-testid="tf-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={isPreventive ? t('talentFix.descPlaceholderPreventive') : t('talentFix.descPlaceholderReactive')}
              rows={4}
              maxLength={8000}
            />
          </div>

          {/* Scope unit + severity/priority + confidentiality */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">{t('talentFix.scopeUnit')}</Label>
              <Select value={scopeUnit} onValueChange={setScopeUnit}>
                <SelectTrigger data-testid="tf-scope-unit"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCOPES.map((s) => (
                    <SelectItem key={s} value={s}>{t(`talentFix.scope.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {isPreventive ? (
              <div>
                <Label className="text-xs uppercase tracking-wider">{t('talentFix.priority')}</Label>
                <Select value={strategicPriority} onValueChange={setStrategicPriority}>
                  <SelectTrigger data-testid="tf-priority"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((s) => (
                      <SelectItem key={s} value={s}>{t(`talentFix.priorityValue.${s}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <div>
                <Label className="text-xs uppercase tracking-wider">{t('talentFix.severityOrPriority')}</Label>
                <Select value={severity} onValueChange={setSeverity}>
                  <SelectTrigger data-testid="tf-severity"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {SEVERITIES.map((s) => (
                      <SelectItem key={s} value={s}>{t(`talentFix.severity.${s}`)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider">{t('talentFix.confidentiality')}</Label>
              <Select value={confidentiality} onValueChange={setConfidentiality}>
                <SelectTrigger data-testid="tf-confidentiality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {scopeChoices.map((s) => (
                    <SelectItem key={s} value={s}>{t(`talentFix.scopeC.${s}`)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">{t(`talentFix.scopeCHint.${confidentiality}`)}</p>
            </div>
          </div>

          {/* Preventive only — horizon + sponsor */}
          {isPreventive && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="tf-preventive-fields">
              <div>
                <Label className="text-xs uppercase tracking-wider">{t('talentFix.horizon')}</Label>
                <Input
                  type="number"
                  min={1}
                  max={60}
                  data-testid="tf-horizon"
                  value={targetHorizon}
                  onChange={(e) => setTargetHorizon(e.target.value)}
                />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">{t('talentFix.sponsor')}</Label>
                <Input
                  data-testid="tf-sponsor"
                  value={sponsor}
                  onChange={(e) => setSponsor(e.target.value)}
                  placeholder={t('talentFix.sponsorPlaceholder')}
                  maxLength={200}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={submitting} data-testid="tf-cancel">
            {t('talentFix.cancel')}
          </Button>
          <Button onClick={handleSubmit} disabled={submitting} data-testid="tf-submit" className="gap-1.5">
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('talentFix.openCase')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
